import { FaPlus } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { useState, useEffect, useMemo} from 'react';
import './habitpage.css';
import {SortableHabitItem} from "../Components/SortableHabitItem";
import {HabitItem} from "../Components/HabitItem"
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import useFetch from "../hooks/useFetch";

// bit i = weekday i (0 = Mon ... 6 = Sun), matching Python date.weekday(); 127 = every day
const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const ALL_DAYS = 127;
const DIFFICULTY_OPTIONS = ["easy", "medium", "hard"];

export function Habit() {

  const fetchWithAuth = useFetch();

  const DatetoISOString = (Date) => {
        const year = Date.getFullYear();
        const month = String(Date.getMonth() + 1).padStart(2, '0');
        const day = String(Date.getDate()).padStart(2,'0');
        const isoString = year + "-" + month + "-" + day;
        return isoString;
    }

  const [activeId, setActiveId] = useState(null);
  const [addHabit, setaddHabit] = useState(false);
  const [editHabit, setEditHabit] = useState(false);
  const [allHabits, setHabits] = useState([]);
  const [newDescription, setNewDescription] = useState("");
  const [newRepeatDays, setNewRepeatDays] = useState(ALL_DAYS);
  const [newDifficulty, setNewDifficulty] = useState("medium");
  const [isAdding, setIsAdding] = useState(false);
  const [editingHabitID, setEditingHabitID] = useState(null);
  const [today, setToday] = useState(() => (DatetoISOString(new Date())));
  const [selectedDate, setSelectedDate] = useState(today);
  const isFuture = (new Date(selectedDate)) > (new Date(today));
  const [habitError, setHabitError] = useState("");
  const [previousDescriptions, setPreviousDescriptions] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    // touch devices without pointer events; a short hold distinguishes drag from scroll
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // updates state variable today but not selecteday
  useEffect(() => {
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0) - now //midnight next day

    const timeout = setTimeout(() => {
      setToday(DatetoISOString(new Date()));
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  },[today]);

  useEffect(() => {
    let dateToFetch = selectedDate;
            if (isFuture){
                dateToFetch = today;
            }
    fetchWithAuth(`/habits/${dateToFetch}/`, {
      method: "GET"
    })
    .then( response => response.json())
    .then(data => setHabits(data.habits))
    .catch(error => console.error(error))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today, selectedDate, isFuture]);

  const handleAddHabit = () => {
    if (isFuture){return;}
    if (newRepeatDays === 0) {
      setHabitError("Select at least one repeat day.");
      return;
    }
    setHabitError(""); // Clear previous errors
    const newHabit = {
      description: newDescription,
      done: false,
      date: selectedDate,
      repeat_days: newRepeatDays,
      difficulty: newDifficulty
    }
    setIsAdding(true);
    fetchWithAuth("/habits/", {
      method: "POST",
      body: JSON.stringify(newHabit)
    })
    .then(async response => {
      if (response.status === 409) {
        setHabitError("A habit with this description already exists.");
        throw new Error("Duplicate habit");
      }
      return response.json();
    })
    .then( data => {
      setHabits(allHabits => [...allHabits, data]);
      setNewDescription("");
      setNewRepeatDays(ALL_DAYS);
      setNewDifficulty("medium");
      setaddHabit(false);
    }
    )
    .catch(error => console.error(error))
    .finally(() => setIsAdding(false))
  };

  // habits already on the day would just hit the duplicate guard, and since habits carry
  // forward they would otherwise bury the dormant ones this dropdown exists to surface.
  // A memo over live state, so deleting a habit makes it reappear here immediately.
  const reusableDescriptions = useMemo(() => {
    const onThisDay = new Set(allHabits.map(habit => habit.description));
    return previousDescriptions.filter(previous => !onThisDay.has(previous.description));
  }, [previousDescriptions, allHabits]);

  // "reuse previous" dropdown: prefills the add form with a prior description, its repeat
  // days and its difficulty. The description must be inserted byte-identical — stats group
  // by exact string, so any normalization here would re-split the history this preserves.
  const prefillFromPrevious = (description) => {
    const previous = previousDescriptions.find(p => p.description === description);
    if (!previous) return;
    setNewDescription(previous.description);
    setNewRepeatDays(previous.repeat_days ?? ALL_DAYS);
    setNewDifficulty(previous.difficulty ?? "medium");
  }

  const handleDeleteHabit = (index) => {
    if (isFuture){return;}
    fetchWithAuth(`/habits/${index}/`, {
      method: "DELETE"
    })
    .then(response => response.json())
    .then(data =>
      setHabits(allHabits => allHabits.filter(habit => (habit.id !== data.id)))
    )
    .catch(error => console.error(error))
  }

  const handleEditHabit = () => {
    if (isFuture){return;}
    if (isAdding) return;
    if (editingHabitID === null) return;
    if (newRepeatDays === 0) {
      setHabitError("Select at least one repeat day.");
      return;
    }
    setHabitError(""); // Clear previous errors

    const newHabit = {
      description: newDescription,
      repeat_days: newRepeatDays,
      difficulty: newDifficulty
    }

    setIsAdding(true);
    fetchWithAuth(`/habits/${editingHabitID}/`,{
      method: 'PUT',
      body: JSON.stringify(newHabit)
    })
    .then(async response => {
      if (response.status === 409) {
        setHabitError("A habit with this description already exists.");
        throw new Error("Duplicate habit");
      }
      return response.json();
    })
    .then(data => {
      setHabits(allHabits =>
        allHabits.map(habit =>
          (habit.id === data.id) ? data : habit
        ));
      setNewDescription("");
      setNewRepeatDays(ALL_DAYS);
      setNewDifficulty("medium");
      setEditHabit(false);
      setEditingHabitID(null);
    }
    )
    .catch(error => console.error(error))
    .finally(() => setIsAdding(false))
  }

  const closeEditHabit = () => {
    if (editingHabitID === null) {
      setEditHabit(false);
      setNewDescription("");
      setNewRepeatDays(ALL_DAYS);
      setNewDifficulty("medium");
      setHabitError("");
      return;
    }
    handleEditHabit();
  }

  const handleToggleHabit = (index, currStatus) => {
    if (isFuture){return;}
    const newHabit = {
      done: !currStatus
    }
    fetchWithAuth(`/habits/${index}/`,{
      method: "PUT",
      body: JSON.stringify(newHabit)
    })
    .then(response => response.json())
    .then(data => {
      setHabits(allHabits => 
        allHabits.map(habit =>
          (habit.id === data.id) ? data : habit
        ))
    }
    )
    .catch(error => console.error(error))
  }

  const renderWeekdayPicker = () => (
    <>
      <label>Repeat on</label>
      <div className="weekday-picker">
        {WEEKDAY_LABELS.map((label, i) => (
          <button type="button" key={label}
            className={`weekday-toggle ${(newRepeatDays & (1 << i)) ? 'selected' : ''}`}
            onClick={() => setNewRepeatDays(prev => prev ^ (1 << i))}>
            {label}
          </button>
        ))}
      </div>
      {newRepeatDays === 0 && (
        <div style={{ color: "red", marginBottom: "10px" }}>Select at least one day.</div>
      )}
    </>
  );

  const renderDifficultyPicker = () => (
    <>
      <label>Difficulty</label>
      <div className="difficulty-picker">
        {DIFFICULTY_OPTIONS.map(option => (
          <button type="button" key={option}
            className={`difficulty-toggle ${option} ${newDifficulty === option ? 'selected' : ''}`}
            onClick={() => setNewDifficulty(option)}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </button>
        ))}
      </div>
    </>
  );

  function handleDragStart(event) {
    const {active} = event;
    
    setActiveId(active.id);
  }
  
  function handleDragEnd(event) {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      const oldIndex = allHabits.findIndex(habit => habit.id === active.id);
      const newIndex = allHabits.findIndex(habit => habit.id === over.id);
      const reordered = arrayMove(allHabits, oldIndex, newIndex);
      setHabits(reordered);

      if (!isFuture) {
        fetchWithAuth("/habits/reorder/", {
          method: "PATCH",
          body: JSON.stringify({ date: selectedDate, order: reordered.map(habit => habit.id) })
        })
        .catch(error => console.error(error));
      }
    }

    setActiveId(null);
  }


  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="App">
        <div className="date-slider-container">
              <label htmlFor="date-slider">Select Date: </label>
              <input
                  type="date"
                  id="date-slider"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
              />
          </div>
        <h1>My Habits</h1>
        <div className="habit-wrapper">
          <button type = 'button' className = 'primaryBtn'
              onClick={() => {
                setaddHabit(true); setNewRepeatDays(ALL_DAYS); setNewDifficulty("medium");
                // feeds the "reuse previous" dropdown with the user's distinct prior descriptions
                fetchWithAuth(`/habits/titles/`, { method: "GET" })
                  .then(response => response.json())
                  .then(data => setPreviousDescriptions(data.titles))
                  .catch(error => console.error(error));
              }} disabled = {isFuture}>
              <FaPlus className = "plus-icon" />
          </button>
          {addHabit && (
            <div className = "habit-input">
              <div className = "habit-input-item">
                <IoMdClose className = "close-icon"
                  onClick={() => {setaddHabit(false); setHabitError("");}}/>
                <h3>Add a New Habit</h3>
                {reusableDescriptions.length > 0 && (
                  <>
                    <label htmlFor="reuse-previous">Reuse previous: </label>
                    <select
                      id="reuse-previous"
                      className="reuse-previous-select"
                      value=""
                      onChange={e => prefillFromPrevious(e.target.value)}
                    >
                      <option value="" disabled>Select a previous habit…</option>
                      {reusableDescriptions.map(previous => (
                        // the label carries the last-active date, but the value stays the raw
                        // description — never parse the description back out of the label
                        <option key={previous.description} value={previous.description}>
                          {previous.description} — last: {previous.date}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label>Description</label>
                <input type= "text" value = {newDescription} 
                onChange={(e) => setNewDescription(e.target.value) }
                onKeyDown={(e) => {
                  if (e.key === "Enter"){
                    handleAddHabit();
                  }
                }}
                placeholder="What's the habit"/>
                {renderWeekdayPicker()}
                {renderDifficultyPicker()}
                <button type = 'button' className = 'addHabitButton'
                  onClick={handleAddHabit}
                  disabled = {isAdding || newRepeatDays === 0}
                  > {isAdding ? "Adding..." : "Add Habit"}</button>
                {habitError && (
                  <div style={{ color: "red", marginBottom: "10px" }}>{habitError}</div>
                )}
              </div>
            </div>
          )}
          {editHabit && (
            <div className = "habit-input">
              <div className = "habit-edit-item">
                <IoMdClose className = "close-icon"
                  onClick={closeEditHabit}/>
                <h3>Edit habit</h3>
                <label>Description</label>
                <input type= "text" value = {newDescription} 
                onChange={(e) => setNewDescription(e.target.value) }
                onKeyDown={(e) => {
                  if (e.key === "Enter"){
                    handleEditHabit();
                  }
                }}
                placeholder="What's the habit"/>
                {renderWeekdayPicker()}
                {renderDifficultyPicker()}
                <button type = 'button' className = 'editHabitButton'
                  onClick={handleEditHabit}
                  disabled = {isAdding || newRepeatDays === 0}
                  > {isAdding ? "Editing..." : "Done"}</button>
                {habitError && (
                <div style={{ color: "red", marginBottom: "10px" }}>{habitError}</div>
                )}
              </div>
            </div>
          )}
          <SortableContext items={allHabits.map(habit => habit.id)} strategy={verticalListSortingStrategy}>
            <div className = "habit-list">
            {allHabits.map((item) =>(
                <SortableHabitItem
                  key={item.id}
                  item={item}
                  isFuture={isFuture}
                  onEdit={item => { setEditHabit(true); setNewDescription(item.description); setNewRepeatDays(item.repeat_days ?? ALL_DAYS); setNewDifficulty(item.difficulty ?? "medium"); setEditingHabitID(item.id); }}
                  onDelete={handleDeleteHabit}
                  onToggle={handleToggleHabit}
                  activeId= {activeId}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      </div>
      <DragOverlay>
        {activeId ? <HabitItem 
            item={allHabits.find(h => h.id === activeId)}
            isFuture={isFuture}
            onEdit={item => { setEditHabit(true); setNewDescription(item.description); setNewRepeatDays(item.repeat_days ?? ALL_DAYS); setNewDifficulty(item.difficulty ?? "medium"); setEditingHabitID(item.id); }}
            onDelete={handleDeleteHabit}
            onToggle={handleToggleHabit} /> : null}
      </DragOverlay>
    </DndContext>
   
  );
}
